import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import ImageGallery from '../components/ImageGallery';
import { PageLayout, Container } from '../components/Layout';
import { Button, Badge, Stars, QtyControl, PageLoader, EmptyState, FormGroup, Label, Textarea } from '../components/UI';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import api, { fmt, imgUrl } from '../utils/api';
import toast from 'react-hot-toast';

const Grid = styled.div`
  display: grid; grid-template-columns: 1fr 1fr; gap: 48px; align-items: start;
  @media(max-width:900px) { grid-template-columns: 1fr; }
`;
const Gallery = styled.div`
  position: sticky; top: calc(var(--header-height) + 20px);
  @media(max-width:900px) { position: static; }
`;
const MainImg = styled.div`
  border-radius: var(--radius-lg); overflow: hidden; aspect-ratio: 1;
  background: var(--gray-100); cursor: zoom-in;
  img { width: 100%; height: 100%; object-fit: cover; transition: transform .4s; }
  &:hover img { transform: scale(1.12); }
`;
const Thumbs = styled.div` display: flex; gap: 10px; margin-top: 12px; overflow-x: auto; padding-bottom: 4px; `;
const Thumb = styled.div`
  width: 70px; height: 70px; border-radius: var(--radius); overflow: hidden;
  cursor: pointer; flex-shrink: 0; border: 2px solid ${p => p.$active ? 'var(--primary)' : 'transparent'};
  transition: var(--transition); img { width: 100%; height: 100%; object-fit: cover; }
  &:hover { border-color: var(--primary); }
`;
const ColorSwatch = styled.button`
  width: 32px; height: 32px; border-radius: 50%;
  border: 3px solid ${p => p.$active ? 'var(--primary)' : 'transparent'};
  cursor: pointer; outline: 2px solid ${p => p.$active ? 'var(--primary)' : 'var(--gray-200)'};
  transition: var(--transition); background: ${p => p.$hex || '#ccc'};
  &:hover { transform: scale(1.15); }
`;
const SizeBtn = styled.button`
  padding: 8px 18px; border-radius: var(--radius-sm);
  border: 1.5px solid ${p => p.$active ? 'var(--primary)' : 'var(--gray-200)'};
  background: ${p => p.$active ? 'var(--primary-50)' : '#fff'};
  color: ${p => p.$active ? 'var(--primary)' : 'var(--gray-700)'};
  font-size: .88rem; font-weight: 500; cursor: pointer; transition: var(--transition);
  &:hover { border-color: var(--primary); color: var(--primary); }
`;
const ReviewCard = styled.div`
  padding: 16px; border-radius: var(--radius-lg); background: var(--gray-50);
  border: 1px solid var(--gray-100); margin-bottom: 12px;
`;

export default function ProductDetail() {
  const { slug } = useParams();
  const { add } = useCart();
  const { isLoggedIn, user } = useAuth();
  const navigate = useNavigate();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImg, setActiveImg] = useState(0);
  const [selectedColor, setSelectedColor] = useState(null);
  const [selectedSize, setSelectedSize] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [qty, setQty] = useState(1);
  const [tab, setTab] = useState('description');
  const [review, setReview] = useState({ rating: 5, title: '', comment: '' });
  const [submittingReview, setSubmittingReview] = useState(false);
  const [wishlisted, setWishlisted] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/products/${slug}`)
      .then(d => {
        setProduct(d.product);
        const colors = [...new Set((d.product.variants || []).map(v => v.color).filter(Boolean))];
        const sizes  = [...new Set((d.product.variants || []).map(v => v.size).filter(Boolean))];
        if (colors[0]) setSelectedColor(colors[0]);
        if (sizes[0])  setSelectedSize(sizes[0]);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [slug]);

  // Find matching variant
  useEffect(() => {
    if (!product) return;
    const v = (product.variants || []).find(x => x.color === selectedColor && x.size === selectedSize);
    setSelectedVariant(v || null);
  }, [selectedColor, selectedSize, product]);

  const colors = product ? [...new Set((product.variants || []).map(v => v.color).filter(Boolean))] : [];
  const sizes  = product ? [...new Set((product.variants || []).filter(v => !selectedColor || v.color === selectedColor).map(v => v.size).filter(Boolean))] : [];
  const allImages = product ? [
    ...(product.images || []).map(i => i.image_url),
    ...(product.primary_image && !(product.images || []).length ? [product.primary_image] : [])
  ] : [];

  const handleAddToCart = async () => {
    if (!product) return;
    await add(product.id, selectedVariant?.id || null, qty, {
      name: product.name, price: product.price, sale_price: product.sale_price,
      image: allImages[0] || product.primary_image, color: selectedColor, size: selectedSize
    });
  };

  const handleWishlist = async () => {
    if (!isLoggedIn) { navigate('/login'); return; }
    try {
      if (wishlisted) { await api.delete(`/wishlist/${product.id}`); setWishlisted(false); toast.success('Removed from wishlist'); }
      else { await api.post(`/wishlist/${product.id}`); setWishlisted(true); toast.success('Added to wishlist'); }
    } catch { toast.error('Failed'); }
  };

  const submitReview = async () => {
    if (!isLoggedIn) { navigate('/login'); return; }
    setSubmittingReview(true);
    try {
      await api.post('/reviews', { product_id: product.id, ...review });
      toast.success('Review submitted!');
      setReview({ rating: 5, title: '', comment: '' });
      // Reload reviews
      const d = await api.get(`/products/${slug}`);
      setProduct(d.product);
    } catch (e) { toast.error(e.message || 'Failed'); }
    finally { setSubmittingReview(false); }
  };

  if (loading) return <PageLayout><PageLoader /></PageLayout>;
  if (!product) return <PageLayout><Container style={{ padding:'60px 20px' }}><EmptyState icon="❌" title="Product not found" /></Container></PageLayout>;

  const price = product.sale_price || product.price;
  const discount = product.sale_price ? Math.round((1 - product.sale_price / product.price) * 100) : 0;

  return (
    <PageLayout>
      <Container style={{ padding:'40px 20px' }}>
        <Grid>
          {/* Gallery */}
          <Gallery>
            <MainImg>
              <img src={imgUrl(allImages[activeImg] || product.primary_image)} alt={product.name} />
            </MainImg>
            {allImages.length > 1 && (
              <Thumbs>
                {allImages.map((img, i) => (
                  <Thumb key={i} $active={activeImg === i} onClick={() => setActiveImg(i)}>
                    <img src={imgUrl(img)} alt={`View ${i+1}`} />
                  </Thumb>
                ))}
              </Thumbs>
            )}
          </Gallery>

          {/* Info */}
          <div>
            <div style={{ fontSize:'0.82rem', color:'var(--primary-light)', fontWeight:600, textTransform:'uppercase', letterSpacing:1 }}>{product.category_name}</div>
            <h1 style={{ fontSize:'clamp(1.5rem,3vw,2.2rem)', margin:'8px 0 12px' }}>{product.name}</h1>

            <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:16 }}>
              <Stars rating={product.avg_rating} count={product.review_count} />
              <span style={{ fontSize:'0.85rem', color:'var(--gray-500)' }}>
                {product.stock > 0 ? <span style={{ color:'var(--success)', fontWeight:600 }}>✓ In Stock ({product.stock})</span> : <span style={{ color:'var(--error)' }}>✗ Out of Stock</span>}
              </span>
            </div>

            <div style={{ display:'flex', alignItems:'baseline', gap:12, marginBottom:24 }}>
              <span style={{ fontSize:'2rem', fontWeight:700, color:'var(--primary)' }}>{fmt.currency(price)}</span>
              {product.sale_price && <span style={{ fontSize:'1.2rem', color:'var(--gray-400)', textDecoration:'line-through' }}>{fmt.currency(product.price)}</span>}
              {discount > 0 && <Badge $color="error">-{discount}%</Badge>}
            </div>

            {product.short_description && (
              <p style={{ color:'var(--gray-600)', marginBottom:20, lineHeight:1.7 }}>{product.short_description}</p>
            )}

            {/* Color variants */}
            {colors.length > 0 && (
              <div style={{ marginBottom:20 }}>
                <div style={{ fontSize:'0.88rem', fontWeight:600, color:'var(--gray-700)', marginBottom:10 }}>
                  Color: <span style={{ fontWeight:400 }}>{selectedColor}</span>
                </div>
                <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
                  {colors.map(c => {
                    const variantWithHex = (product.variants || []).find(v => v.color === c);
                    return (
                      <ColorSwatch key={c} $active={selectedColor === c} $hex={variantWithHex?.color_hex}
                        onClick={() => setSelectedColor(c)} title={c} />
                    );
                  })}
                </div>
              </div>
            )}

            {/* Size variants */}
            {sizes.length > 0 && (
              <div style={{ marginBottom:20 }}>
                <div style={{ fontSize:'0.88rem', fontWeight:600, color:'var(--gray-700)', marginBottom:10 }}>Size:</div>
                <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                  {sizes.map(s => <SizeBtn key={s} $active={selectedSize === s} onClick={() => setSelectedSize(s)}>{s}</SizeBtn>)}
                </div>
              </div>
            )}

            {/* Qty + Add to Cart */}
            <div style={{ display:'flex', gap:12, marginBottom:16, flexWrap:'wrap' }}>
              <QtyControl value={qty} onChange={setQty} max={product.stock || 99} />
              <Button $size="lg" onClick={handleAddToCart} disabled={product.stock === 0} style={{ flex:1, minWidth:160 }}>
                🛒 Add to Cart
              </Button>
              <Button $variant={wishlisted ? 'danger' : 'outline'} $size="lg" onClick={handleWishlist} style={{ width:50, padding:0, justifyContent:'center' }}>
                {wishlisted ? '♥' : '♡'}
              </Button>
            </div>

            {/* Meta */}
            <div style={{ borderTop:'1px solid var(--gray-100)', paddingTop:16 }}>
              {product.sku && <div style={{ fontSize:'0.85rem', marginBottom:6 }}><strong>SKU:</strong> {product.sku}</div>}
              {product.category_name && <div style={{ fontSize:'0.85rem', marginBottom:6 }}><strong>Category:</strong> {product.category_name}</div>}
              <div style={{ fontSize:'0.85rem', marginBottom:6 }}>
                <strong>Payment:</strong> PayPal · Secure Checkout
              </div>
            </div>
          </div>
        </Grid>

        {/* Tabs */}
        <div style={{ marginTop:48 }}>
          <div style={{ display:'flex', borderBottom:'2px solid var(--gray-100)', marginBottom:28, gap:4, overflowX:'auto' }}>
            {[['description','Description'],['reviews','Reviews ('+product.review_count+')']].map(([v,l]) => (
              <button key={v} onClick={() => setTab(v)}
                style={{ padding:'10px 22px', fontSize:'.9rem', fontWeight:500, cursor:'pointer', border:'none', background:'none', borderBottom: tab===v ? '2px solid var(--primary)' : '2px solid transparent', marginBottom:'-2px', color: tab===v ? 'var(--primary)' : 'var(--gray-500)', transition:'var(--transition)', whiteSpace:'nowrap' }}>
                {l}
              </button>
            ))}
          </div>

          {tab === 'description' && (
            <div style={{ color:'var(--gray-600)', lineHeight:1.8, maxWidth:800 }} dangerouslySetInnerHTML={{ __html: product.description || '<p>No description available.</p>' }} />
          )}

          {tab === 'reviews' && (
            <div style={{ maxWidth:800 }}>
              {/* Write review */}
              <div style={{ background:'#fff', borderRadius:'var(--radius-lg)', padding:24, border:'1px solid var(--gray-100)', marginBottom:24 }}>
                <h3 style={{ marginBottom:16 }}>Write a Review</h3>
                <FormGroup>
                  <Label>Rating</Label>
                  <div style={{ display:'flex', gap:8 }}>
                    {[1,2,3,4,5].map(n => (
                      <button key={n} onClick={() => setReview(r=>({...r,rating:n}))}
                        style={{ background:'none', border:'none', cursor:'pointer', fontSize:'1.5rem', color: n <= review.rating ? '#f59e0b' : 'var(--gray-300)' }}>★</button>
                    ))}
                  </div>
                </FormGroup>
                <FormGroup>
                  <Label>Title</Label>
                  <input className="form-control" value={review.title} onChange={e => setReview(r=>({...r,title:e.target.value}))} placeholder="Review title" style={{ width:'100%', padding:'10px 14px', border:'1.5px solid var(--gray-200)', borderRadius:'var(--radius-sm)', fontFamily:'var(--font-body)' }} />
                </FormGroup>
                <FormGroup>
                  <Label>Comment</Label>
                  <Textarea value={review.comment} onChange={e => setReview(r=>({...r,comment:e.target.value}))} placeholder="Share your experience..." />
                </FormGroup>
                <Button onClick={submitReview} disabled={submittingReview}>{submittingReview ? 'Submitting...' : 'Submit Review'}</Button>
              </div>

              {/* Reviews list */}
              {(product.reviews || []).length === 0
                ? <EmptyState icon="💬" title="No reviews yet" subtitle="Be the first to review this product!" />
                : (product.reviews || []).map(r => (
                  <ReviewCard key={r.id}>
                    <div style={{ display:'flex', justifyContent:'space-between', marginBottom:8 }}>
                      <div>
                        <strong style={{ fontSize:'0.9rem' }}>{r.customer_name}</strong>
                        <Stars rating={r.rating} />
                      </div>
                      <span style={{ fontSize:'0.8rem', color:'var(--gray-400)' }}>{fmt.date(r.created_at)}</span>
                    </div>
                    {r.title && <div style={{ fontWeight:600, marginBottom:4 }}>{r.title}</div>}
                    <p style={{ color:'var(--gray-600)', fontSize:'0.9rem' }}>{r.comment}</p>
                  </ReviewCard>
                ))
              }
            </div>
          )}
        </div>
      </Container>
    </PageLayout>
  );
}
